// Shared utility to filter event objects by allowed keys while always preserving the mandatory 'event' field.
// Generic to keep strong typing for different event payload shapes.

export function filterEventKeys<
  TEvent extends { event: string | number | symbol },
  const TKeys extends readonly (keyof TEvent)[],
>(
  fullEvent: TEvent,
  includedKeys?: TKeys,
): Pick<TEvent, "event" | Extract<TKeys[number], keyof TEvent>> | TEvent {
  if (!includedKeys || includedKeys.length === 0) {
    // filter out undefined/null values but keep all keys
    const cloned: Partial<TEvent> = {};
    for (const k in fullEvent) {
      const v = fullEvent[k];
      if (v !== undefined && v !== null) {
        cloned[k] = v;
      }
    }
    return cloned as TEvent;
  }

  // Start with mandatory event type; add selected properties if defined
  const filtered: Partial<TEvent> = {
    event: fullEvent.event,
  } as Partial<TEvent>;
  for (const key of includedKeys) {
    if (key === "event") continue;
    const value = fullEvent[key];
    if (value !== undefined && value !== null) {
      filtered[key] = value;
    }
  }
  return filtered as Pick<
    TEvent,
    "event" | Extract<TKeys[number], keyof TEvent>
  >;
}

export type IncludedKeys<T> = readonly (keyof T)[];
